<template>
    <div class="pt-[20px] bg-[#ededed]">
        <div class="max-w-[1400px] w-full mx-auto">
            <div class="pt-[60px] pr-[20px] pb-[2%] pl-[10px] max-w-[1400px]" id="niceNavigation">
                <niceNavigation />
            </div>
        </div>
    </div>

    <div class="max-w-[1400px] w-full container mx-auto">
        <div class="pt-[30px] pr-[20px] pb-[40px] pl-[10px] max-w-[1400px]" id="niceForm">
            <!-- Video Deduplication Control Panel -->
            <div class="bg-white rounded-lg shadow-md p-6 mb-8">
                <h2 class="text-left text-2xl font-medium mb-[3%]">
                    <strong>Steuerzentrale für Videoduplikate</strong>
                </h2>

                <!-- Debug Mode Toggle -->
                <div
                    v-if="showDebugToggle"
                    class="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
                >
                    <div class="flex items-center justify-between">
                        <div>
                            <h3 class="text-lg font-medium text-yellow-800">Debug-Modus</h3>
                            <p class="text-sm text-yellow-600">
                                Verwendet Dummy-Daten anstelle echter Backend-Aufrufe
                            </p>
                        </div>
                        <v-switch
                            v-model="debugMode"
                            color="warning"
                            :disabled="dedupLoading || isExecuting"
                        />
                    </div>
                </div>

                <v-form @submit.prevent="executeDeduplication" ref="formRef">
                    <v-container fluid>
                        <!-- User ID Input -->
                        <div class="mb-6">
                            <FormInput
                                v-model="userIdInput"
                                label="Benutzer‑ID"
                                placeholder="Geben Sie die Benutzer-ID ein"
                                :disabled="dedupLoading"
                                required
                                style="width: 100%"
                            />
                        </div>

                        <!-- Advanced Options Toggle -->
                        <div class="mb-6" v-if="isDebugModeActive || showAdvancedOptions">
                            <h2 class="my-[2.5%] text-xl font-normal" id="AdvancedOptions">
                                <span
                                    class="cursor-pointer"
                                    @click="showAdvancedOptions = !showAdvancedOptions"
                                >
                                    Erweiterte Optionen
                                    <i
                                        class="bi-chevron-down ml-[5px] text-[#db0720]"
                                        v-if="showAdvancedOptions"
                                    ></i>
                                    <i class="bi-chevron-right ml-[5px] text-[#db0720]" v-else></i>
                                </span>
                            </h2>

                            <v-container fluid v-if="showAdvancedOptions">
                                <div class="bg-gray-50 p-4 rounded">
                                    <div class="mb-4">
                                        <FormSelect
                                            v-model="similarityMethod"
                                            :items="[
                                                { key: 'default', value: 'Kombiniert (Standard)' },
                                                { key: 'visual', value: 'Visuelle Merkmale' },
                                                { key: 'audio', value: 'Audiodaten' },
                                                { key: 'metadata', value: 'Metadaten' },
                                            ]"
                                            :item-value="(item) => item.key"
                                            :item-text="(item) => item.value"
                                            :item-title="(item) => item.value"
                                            label="Ähnlichkeitsmethode"
                                            :disabled="dedupLoading"
                                        />
                                    </div>

                                    <div class="mb-4">
                                        <FormSelect
                                            v-model="processingPriority"
                                            :items="[
                                                { key: 'normal', value: 'Normal' },
                                                { key: 'high', value: 'Hoch' },
                                                { key: 'low', value: 'Niedrig' },
                                            ]"
                                            :item-value="(item) => item.key"
                                            :item-text="(item) => item.value"
                                            :item-title="(item) => item.value"
                                            label="Verarbeitungspriorität"
                                            :disabled="dedupLoading"
                                        />
                                    </div>

                                    <div class="flex items-center mb-4">
                                        <v-checkbox
                                            id="useCache"
                                            v-model="useCache"
                                            color="#db0720"
                                            label="Cache verwenden (schneller, falls verfügbar)"
                                            :disabled="dedupLoading"
                                        />
                                    </div>

                                    <div class="flex items-center">
                                        <v-checkbox
                                            id="savePrefs"
                                            v-model="savePreferences"
                                            color="#db0720"
                                            label="Diese Einstellungen speichern"
                                            :disabled="dedupLoading"
                                        />
                                    </div>
                                </div>
                            </v-container>
                        </div>

                        <!-- Action Buttons -->
                        <div class="flex flex-wrap gap-3 mb-6" style="width: 100%">
                            <FullButton
                                :handleClick="executeDeduplication"
                                :title="
                                    dedupLoading || isExecuting
                                        ? 'Lädt...'
                                        : 'Deduplikation starten'
                                "
                                style="width: 49%"
                                width="100"
                                :colour="dedupLoading || isExecuting ? 'red' : 'red'"
                            />

                            <FullButton
                                v-if="dedupLoading || isExecuting"
                                :handleClick="cancelDeduplication"
                                title="Deduplikation abbrechen"
                                style="width: 49%"
                                width="100"
                                colour="gray"
                            />

                            <FullButton
                                v-if="!dedupLoading && !isExecuting"
                                :handleClick="resetEverything"
                                title="Zurücksetzen"
                                style="width: 49%"
                                width="100"
                                colour="gray"
                            />
                        </div>
                    </v-container>
                </v-form>

                <!-- Progress Indicator -->
                <div v-if="hasRun" class="bg-white rounded-lg p-4 mb-6 border border-gray-200">
                    <div class="flex flex-col space-y-4">
                        <!-- Progress bar -->
                        <div>
                            <div class="flex justify-between items-center mb-1">
                                <div class="flex items-center">
                                    <span class="font-medium">Status:</span>
                                    <span class="ml-2 font-semibold">{{
                                        stageName || 'Initialisiere...'
                                    }}</span>
                                    <span v-if="substageName" class="ml-1 text-sm text-gray-500"
                                        >({{ substageName }})</span
                                    >
                                    <span
                                        v-if="isDebugModeActive"
                                        class="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded"
                                        >DEBUG</span
                                    >
                                    <span v-if="currentStage === 'error'" class="ml-2 text-red-600"
                                        >●</span
                                    >
                                    <span
                                        v-else-if="dedupLoading"
                                        class="ml-2 animate-pulse text-blue-600"
                                        >●</span
                                    >
                                    <span v-else-if="progress >= 99" class="ml-2 text-green-600"
                                        >●</span
                                    >
                                </div>
                                <div>
                                    <span class="font-bold">{{ progressDisplay }} %</span>
                                </div>
                            </div>

                            <v-progress-linear
                                :model-value="safeProgressWidth"
                                :color="
                                    errorMessage ? 'error' : progress >= 99 ? 'success' : 'primary'
                                "
                                height="16"
                                rounded
                            ></v-progress-linear>
                        </div>

                        <!-- Detailed status message -->
                        <div class="text-sm text-gray-700 flex items-center">
                            <span>{{ detailedStatusMessage }}</span>
                            <span
                                v-if="dedupLoading && !errorMessage"
                                class="ml-2 animate-pulse text-blue-500"
                                >⏱️</span
                            >
                        </div>

                        <!-- Processing Stats -->
                        <div
                            v-if="hasProcessingStats"
                            class="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2"
                        >
                            <div
                                v-if="processingStats.extraction_speed > 0"
                                class="text-xs text-gray-600 bg-gray-50 p-2 rounded"
                            >
                                <span class="font-medium">Extraktionsgeschwindigkeit:</span>
                                {{ processingStats.extraction_speed.toFixed(1) }} Videos/min
                            </div>
                            <div
                                v-if="processingStats.comparison_speed > 0"
                                class="text-xs text-gray-600 bg-gray-50 p-2 rounded"
                            >
                                <span class="font-medium">Vergleichsgeschwindigkeit:</span>
                                {{ processingStats.comparison_speed.toFixed(1) }} Paare/min
                            </div>
                            <div
                                v-if="
                                    processingStats.feature_cache_hits > 0 ||
                                    processingStats.feature_cache_misses > 0
                                "
                                class="text-xs text-gray-600 bg-gray-50 p-2 rounded"
                            >
                                <span class="font-medium">Cache:</span> {{ cacheStatusText }}
                            </div>
                            <div
                                v-if="processingStats.current_memory_usage_mb > 0"
                                class="text-xs text-gray-600 bg-gray-50 p-2 rounded"
                            >
                                <span class="font-medium">Speicherverbrauch:</span>
                                {{ processingStats.current_memory_usage_mb.toFixed(1) }} MB
                                <span v-if="processingStats.peak_memory_usage_mb > 0"
                                    >(Spitze:
                                    {{ processingStats.peak_memory_usage_mb.toFixed(1) }} MB)</span
                                >
                            </div>
                        </div>

                        <!-- Stage description -->
                        <div
                            v-if="currentStage"
                            class="mt-1 text-sm text-gray-600 bg-gray-50 p-3 rounded"
                        >
                            <div class="font-medium mb-1">{{ stageName }}:</div>
                            <div>{{ stageDescription }}</div>
                            <div v-if="currentSubstage && substageDescription" class="mt-2 text-xs">
                                <span class="font-medium">{{ substageName }}:</span>
                                {{ substageDescription }}
                            </div>
                        </div>

                        <!-- Activity log -->
                        <div
                            v-if="showActivityLog && latestActivities.length > 0"
                            class="mt-1 text-sm text-gray-600 bg-gray-50 p-3 rounded"
                        >
                            <div class="flex justify-between items-center mb-2">
                                <div class="font-medium">Letzte Aktivitäten:</div>
                                <v-btn
                                    @click="showActivityLog = !showActivityLog"
                                    size="small"
                                    variant="text"
                                    color="primary"
                                >
                                    Verbergen
                                </v-btn>
                            </div>
                            <ul class="text-xs space-y-1">
                                <li
                                    v-for="(activity, idx) in latestActivities"
                                    :key="idx"
                                    class="border-b border-gray-100 pb-1"
                                >
                                    <span class="text-gray-500">{{
                                        formatTimestamp(activity.timestamp)
                                    }}</span>
                                    <span class="ml-1">{{ activity.activity }}</span>
                                </li>
                            </ul>
                        </div>
                        <div v-else-if="latestActivities.length > 0" class="mt-1">
                            <v-btn
                                @click="showActivityLog = true"
                                size="small"
                                variant="text"
                                color="primary"
                            >
                                Aktivitätsprotokoll anzeigen
                            </v-btn>
                        </div>

                        <!-- Error message -->
                        <div
                            v-if="errorMessage"
                            class="mt-1 text-sm text-red-600 bg-red-50 p-3 rounded"
                        >
                            <div class="font-medium">Fehler:</div>
                            <div>{{ errorMessage }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Deduplication Results -->
            <div
                v-if="deduplicationReport && !dedupLoading"
                class="bg-white rounded-lg shadow-md p-6 mb-8"
            >
                <div class="flex justify-between items-center mb-6">
                    <div class="flex items-center space-x-3">
                        <div
                            class="w-10 h-10 bg-[#db0720] text-white rounded-xl flex items-center justify-center"
                        >
                            <i class="bi-check2-circle text-lg"></i>
                        </div>
                        <div>
                            <h2 class="text-3xl font-bold text-gray-800">
                                Deduplikationsergebnisse
                            </h2>
                            <div class="flex items-center space-x-2 mt-1">
                                <span
                                    v-if="isDebugModeActive"
                                    class="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full"
                                >
                                    DEBUG DATEN
                                </span>
                                <span class="text-sm text-gray-600">
                                    Abgeschlossen um {{ new Date().toLocaleTimeString('de-DE') }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Export Options -->
                    <div class="flex space-x-2">
                        <button
                            @click="exportResults('csv')"
                            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                        >
                            CSV
                        </button>
                        <button
                            @click="exportResults('json')"
                            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                        >
                            JSON
                        </button>
                        <button
                            @click="exportResults('pdf')"
                            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                        >
                            PDF
                        </button>
                    </div>
                </div>

                <!-- Summary Stats -->
                <div class="mb-8">
                    <h3 class="text-2xl font-bold mb-6 text-gray-800">Zusammenfassung</h3>
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
                        <div
                            class="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6 text-center"
                        >
                            <div class="text-sm font-medium text-blue-600 mb-2">
                                Gesamtzahl Videos
                            </div>
                            <div class="text-3xl font-bold text-blue-800">
                                {{ deduplicationReport.stats?.total_videos || 0 }}
                            </div>
                        </div>
                        <div
                            class="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-6 text-center"
                        >
                            <div class="text-sm font-medium text-purple-600 mb-2">
                                Duplikatgruppen
                            </div>
                            <div class="text-3xl font-bold text-purple-800">
                                {{
                                    deduplicationReport.stats?.duplicate_clusters ||
                                    deduplicationReport.duplicate_clusters?.length ||
                                    0
                                }}
                            </div>
                        </div>
                        <div
                            class="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-6 text-center"
                        >
                            <div class="text-sm font-medium text-orange-600 mb-2">
                                Duplikatpaare
                            </div>
                            <div class="text-3xl font-bold text-orange-800">
                                {{ deduplicationReport.stats?.duplicate_pairs || 0 }}
                            </div>
                        </div>
                        <div
                            class="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-6 text-center"
                        >
                            <div class="text-sm font-medium text-green-600 mb-2">
                                Verarbeitungszeit
                            </div>
                            <div class="text-3xl font-bold text-green-800">
                                {{
                                    formatProcessingTime(
                                        deduplicationReport.stats?.processing_time_seconds || 0
                                    )
                                }}
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Stage Times (if available) -->
                <div v-if="false" class="mb-8">
                    <div class="flex justify-between items-center mb-6">
                        <h3 class="text-2xl font-bold text-gray-800">Verarbeitungszeiten</h3>
                        <button
                            @click="showStageTimes = !showStageTimes"
                            class="flex items-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors duration-200"
                        >
                            {{ showStageTimes ? 'Verbergen' : 'Details anzeigen' }}
                            <i
                                class="bi-chevron-down ml-2 text-[#db0720] transition-transform duration-200"
                                :class="{ 'rotate-180': !showStageTimes }"
                            ></i>
                        </button>
                    </div>

                    <div
                        v-if="showStageTimes"
                        class="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm"
                    >
                        <div
                            class="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200"
                        >
                            <div class="grid grid-cols-3 gap-4 font-semibold text-sm text-gray-700">
                                <div>Phase</div>
                                <div>Zeit</div>
                                <div>Anteil</div>
                            </div>
                        </div>
                        <div class="divide-y divide-gray-100">
                            <div
                                v-for="(time, stage) in stageTimes"
                                :key="stage"
                                v-if="time && typeof time === 'number' && time > 0"
                                class="px-6 py-4 hover:bg-gray-50 transition-colors duration-200"
                            >
                                <div class="grid grid-cols-3 gap-4 items-center">
                                    <div class="font-medium text-gray-900">
                                        {{ getStageDisplayName(stage) }}
                                    </div>
                                    <div class="text-gray-600">
                                        {{ formatTime(time) }}
                                    </div>
                                    <div class="text-gray-600">
                                        <span
                                            class="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full"
                                        >
                                            {{ calculatePercentage(time) }}%
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Duplicate Clusters -->
                <div class="mb-6">
                    <div class="flex justify-between items-center">
                        <h3 class="text-2xl font-bold text-gray-800">Duplikatgruppen</h3>
                        <button
                            @click="showClusters = !showClusters"
                            class="flex items-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors duration-200"
                        >
                            {{ showClusters ? 'Gruppen verbergen' : 'Gruppen anzeigen' }}
                            <i
                                class="bi-chevron-down ml-2 text-[#db0720] transition-transform duration-200"
                                :class="{ 'rotate-180': !showClusters }"
                            ></i>
                        </button>
                    </div>
                </div>

                <!-- Cluster List -->
                <div
                    v-if="showClusters && deduplicationReport.duplicate_clusters"
                    class="space-y-8"
                >
                    <div
                        v-for="(cluster, index) in deduplicationReport.duplicate_clusters"
                        :key="`cluster-${index}`"
                        class="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300"
                    >
                        <!-- Cluster Header -->
                        <div
                            class="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200"
                        >
                            <div class="flex justify-between items-center">
                                <div class="flex items-center space-x-3">
                                    <div
                                        class="w-8 h-8 bg-[#db0720] text-white rounded-full flex items-center justify-center font-bold text-sm"
                                    >
                                        {{ index + 1 }}
                                    </div>
                                    <h4 class="text-xl font-bold text-gray-800">
                                        Gruppe #{{ index + 1 }}
                                    </h4>
                                </div>
                                <div class="flex items-center space-x-2">
                                    <span
                                        class="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full"
                                    >
                                        {{ cluster.videos?.length || 0 }} Videos
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Video Cards Grid -->
                        <div class="p-6">
                            <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                                <div
                                    v-for="video in cluster.videos"
                                    :key="video.media_id"
                                    class="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 hover:border-gray-300"
                                >
                                    <!-- Video Thumbnail -->
                                    <div class="relative aspect-video bg-gray-100 overflow-hidden" style="min-height: 200px;">
                                        <img
                                            :src="getThumbnailUrl(video.media_id, video)"
                                            class="absolute inset-0 w-full h-full object-contain"
                                            alt="Video Thumbnail"
                                        />
                                        <!-- Duration Badge -->
                                        <div class="absolute bottom-2 right-2 px-2 py-1 bg-black bg-opacity-75 text-white text-xs font-medium rounded">
                                            {{ video.duration || '0:00' }}
                                        </div>
                                    </div>

                                    <!-- Video Content -->
                                    <div class="p-4">
                                        <!-- Title -->
                                        <h5
                                            class="font-bold text-gray-900 text-lg mb-2 line-clamp-2 leading-tight"
                                        >
                                            {{ video.title }}
                                        </h5>

                                        <!-- Secondary Title -->
                                        <p
                                            class="text-sm font-medium text-gray-600 mb-2 line-clamp-1"
                                        >
                                            {{ video.secondary_title }}
                                        </p>

                                        <!-- Description -->
                                        <p
                                            class="text-sm text-gray-500 mb-4 line-clamp-3 leading-relaxed"
                                        >
                                            {{ video.description }}
                                        </p>

                                        <!-- Similar Videos -->
                                        <div v-if="video.similar_videos?.length" class="mb-4">
                                            <p
                                                class="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide"
                                            >
                                                Ähnliche Videos:
                                            </p>
                                            <div class="space-y-2">
                                                <div
                                                    v-for="similar in video.similar_videos"
                                                    :key="similar.media_id"
                                                    class="flex justify-between items-center py-1"
                                                >
                                                    <span
                                                        class="text-sm text-gray-600 truncate flex-1 mr-3"
                                                    >
                                                        {{
                                                            similar.title
                                                                ? similar.title.length > 20
                                                                    ? similar.title.substring(
                                                                          0,
                                                                          20
                                                                      ) + '...'
                                                                    : similar.title
                                                                : formatMediaId(similar.media_id)
                                                        }}
                                                    </span>
                                                    <span
                                                        class="px-2 py-1 text-xs font-bold rounded-full text-white"
                                                        :class="{
                                                            'bg-green-500':
                                                                similar.similarity >= 0.8,
                                                            'bg-orange-500':
                                                                similar.similarity >= 0.6 &&
                                                                similar.similarity < 0.8,
                                                            'bg-red-500': similar.similarity < 0.6,
                                                        }"
                                                    >
                                                        {{ Math.round(similar.similarity * 100) }}%
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Action Button -->
                                        <button
                                            @click="openCompareModal(video.media_id, cluster)"
                                            class="w-full bg-[#db0720] hover:bg-[#c1061c] text-white font-medium py-2.5 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                                        >
                                            <i class="bi-search text-sm"></i>
                                            <span>Vergleichen</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Video Comparison Modal -->
    <v-dialog v-model="showCompareModal" max-width="1400px" persistent class="comparison-modal">
        <v-card class="rounded-2xl overflow-hidden">
            <!-- Modal Header -->
            <v-card-title
                class="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200"
            >
                <div class="flex justify-between items-center w-full">
                    <div class="flex items-center space-x-3">
                        <div
                            class="w-8 h-8 bg-[#db0720] text-white rounded-full flex items-center justify-center"
                        >
                            <i class="bi-search text-sm"></i>
                        </div>
                        <h3 class="text-2xl font-bold text-gray-800">Duplikate vergleichen</h3>
                    </div>
                    <button
                        @click="showCompareModal = false"
                        class="w-8 h-8 rounded-full hover:bg-gray-200 flex items-center justify-center transition-colors duration-200"
                    >
                        <i class="bi-x text-xl text-gray-600"></i>
                    </button>
                </div>
            </v-card-title>

            <v-card-text v-if="compareData" class="p-6">
                <div class="grid grid-cols-2 gap-8">
                    <!-- Primary Video -->
                    <div class="space-y-4">
                        <!-- Video Header -->
                        <div class="pb-4 border-b border-gray-200">
                            <div class="flex items-start space-x-3">
                                <div
                                    class="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold"
                                >
                                    1
                                </div>
                                <div class="flex-1">
                                    <h4 class="text-lg font-bold text-gray-900 leading-tight">
                                        {{ compareData.primary.title }}
                                    </h4>
                                    <p class="text-sm text-gray-600 mt-1">
                                        {{ compareData.primary.secondary_title }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Video Preview -->
                        <div
                            class="relative aspect-video bg-gray-100 rounded-xl overflow-hidden group cursor-pointer"
                            style="min-height: 200px;"
                            @click="playVideo(compareData.primary.media_id)"
                        >
                            <img
                                :src="getThumbnailUrl(compareData.primary.media_id, compareData.primary)"
                                class="absolute inset-0 w-full h-full object-contain"
                                alt="Primary Video"
                            />
                            <!-- <div
                                class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center"
                            >
                                <div
                                    class="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center transform scale-90 group-hover:scale-100 transition-transform duration-300"
                                >
                                    <i class="bi-play-fill text-2xl text-gray-700 ml-1"></i>
                                </div>
                            </div> This play button causes a lot of trouble -->
                            <!-- Duration Badge -->
                            <div
                                class="absolute bottom-3 right-3 px-3 py-1 bg-black bg-opacity-75 text-white text-sm font-medium rounded-lg"
                            >
                                {{ compareData.primary.duration || 'Unbekannt' }}
                            </div>
                        </div>

                        <!-- Description -->
                        <div class="bg-gray-50 rounded-lg p-4">
                            <h5 class="font-semibold text-gray-700 mb-2">Beschreibung</h5>
                            <p class="text-sm text-gray-600 leading-relaxed">
                                {{ compareData.primary.description }}
                            </p>
                        </div>

                        <!-- Metadata -->
                        <div class="grid grid-cols-2 gap-4 text-sm">
                            <div class="bg-gray-50 rounded-lg p-3">
                                <span class="font-semibold text-gray-700">Dauer:</span>
                                <span class="ml-2 text-gray-600">{{
                                    compareData.primary.duration || 'Unbekannt'
                                }}</span>
                            </div>
                            <div class="bg-gray-50 rounded-lg p-3">
                                <span class="font-semibold text-gray-700">Sprache(n):</span>
                                <span class="ml-2 text-gray-600">{{
                                    formatLanguages(compareData.primary.languages)
                                }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Comparison Video -->
                    <div class="space-y-4">
                        <!-- Video Header with Dropdown -->
                        <div class="pb-4 border-b border-gray-200">
                            <div class="flex items-start justify-between">
                                <div class="flex items-start space-x-3 flex-1">
                                    <div
                                        class="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold"
                                    >
                                        2
                                    </div>
                                    <div class="flex-1">
                                        <h4 class="text-lg font-bold text-gray-900 leading-tight">
                                            {{ compareData.comparison.title }}
                                        </h4>
                                        <p class="text-sm text-gray-600 mt-1">
                                            {{ compareData.comparison.secondary_title }}
                                        </p>
                                    </div>
                                </div>
                                <div class="ml-4">
                                    <v-select
                                        v-model="selectedComparisonId"
                                        :items="compareData.otherVideos"
                                        item-value="media_id"
                                        item-title="title"
                                        variant="outlined"
                                        density="compact"
                                        class="min-w-[200px]"
                                        @update:modelValue="updateComparisonVideo"
                                    >
                                        <template v-slot:item="{ props, item }">
                                            <v-list-item
                                                v-bind="props"
                                                :title="`${item.raw.title.length > 25 ? item.raw.title.substring(0, 25) + '...' : item.raw.title}`"
                                                :subtitle="`${Math.round(item.raw.similarity * 100)}% Ähnlichkeit`"
                                            >
                                            </v-list-item>
                                        </template>
                                    </v-select>
                                </div>
                            </div>
                        </div>

                        <!-- Video Preview -->
                        <div
                            class="relative aspect-video bg-gray-100 rounded-xl overflow-hidden group cursor-pointer"
                            style="min-height: 200px;"
                            @click="playVideo(compareData.comparison.media_id)"
                        >
                            <img
                                :src="getThumbnailUrl(compareData.comparison.media_id, compareData.comparison)"
                                class="absolute inset-0 w-full h-full object-contain"
                                alt="Comparison Video"
                            />
                            <!-- Duration Badge -->
                            <div
                                class="absolute bottom-3 right-3 px-3 py-1 bg-black bg-opacity-75 text-white text-sm font-medium rounded-lg"
                            >
                                {{ compareData.comparison.duration || 'Unbekannt' }}
                            </div>
                        </div>

                        <!-- Description -->
                        <div class="bg-gray-50 rounded-lg p-4">
                            <h5 class="font-semibold text-gray-700 mb-2">Beschreibung</h5>
                            <p class="text-sm text-gray-600 leading-relaxed">
                                {{ compareData.comparison.description }}
                            </p>
                        </div>

                        <!-- Metadata -->
                        <div class="grid grid-cols-2 gap-4 text-sm">
                            <div class="bg-gray-50 rounded-lg p-3">
                                <span class="font-semibold text-gray-700">Dauer:</span>
                                <span class="ml-2 text-gray-600">{{
                                    compareData.comparison.duration || 'Unbekannt'
                                }}</span>
                            </div>
                            <div class="bg-gray-50 rounded-lg p-3">
                                <span class="font-semibold text-gray-700">Sprache(n):</span>
                                <span class="ml-2 text-gray-600">{{
                                    formatLanguages(compareData.comparison.languages)
                                }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Similarity Analysis -->
                <div
                    class="mt-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200"
                >
                    <div class="flex items-center space-x-3 mb-6">
                        <div
                            class="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center"
                        >
                            <i class="bi-bar-chart text-sm"></i>
                        </div>
                        <h4 class="text-xl font-bold text-gray-800">Ähnlichkeitsanalyse</h4>
                    </div>

                    <div v-if="compareData.similarityDetails">
                        <!-- Overall Similarity -->
                        <div class="mb-6">
                            <div class="flex justify-between items-center mb-3">
                                <h5 class="text-lg font-semibold text-gray-700">
                                    Gesamtähnlichkeit
                                </h5>
                                <div class="flex items-center space-x-2">
                                    <span
                                        class="px-3 py-1 text-lg font-bold text-white rounded-full"
                                        :class="{
                                            'bg-green-500':
                                                compareData.similarityDetails.overall >= 0.8,
                                            'bg-orange-500':
                                                compareData.similarityDetails.overall >= 0.6 &&
                                                compareData.similarityDetails.overall < 0.8,
                                            'bg-red-500':
                                                compareData.similarityDetails.overall < 0.6,
                                        }"
                                    >
                                        {{
                                            Math.round(compareData.similarityDetails.overall * 100)
                                        }}%
                                    </span>
                                </div>
                            </div>
                            <div class="bg-gray-200 rounded-full h-4">
                                <div
                                    class="h-4 rounded-full transition-all duration-500"
                                    :class="{
                                        'bg-green-500':
                                            compareData.similarityDetails.overall >= 0.8,
                                        'bg-orange-500':
                                            compareData.similarityDetails.overall >= 0.6 &&
                                            compareData.similarityDetails.overall < 0.8,
                                        'bg-red-500': compareData.similarityDetails.overall < 0.6,
                                    }"
                                    :style="{
                                        width: `${compareData.similarityDetails.overall * 100}%`,
                                    }"
                                ></div>
                            </div>
                        </div>

                        <!-- Matching Segments -->
                        <div v-if="compareData.similarityDetails?.matching_segments?.length">
                            <h5 class="text-lg font-semibold text-gray-700 mb-4">
                                Übereinstimmende Segmente
                            </h5>
                            <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                <div
                                    class="grid grid-cols-3 gap-4 p-4 bg-gray-50 border-b border-gray-200 font-semibold text-sm text-gray-700"
                                >
                                    <div>Startzeit</div>
                                    <div>Dauer</div>
                                    <div>Konfidenz</div>
                                </div>
                                <div class="divide-y divide-gray-100">
                                    <div
                                        v-for="(segment, i) in compareData.similarityDetails
                                            .matching_segments"
                                        :key="i"
                                        class="grid grid-cols-3 gap-4 p-4 hover:bg-gray-50 transition-colors duration-200"
                                    >
                                        <div class="text-sm font-medium text-gray-700">
                                            {{ segment.start }}
                                        </div>
                                        <div class="text-sm text-gray-600">
                                            {{ segment.duration }}
                                        </div>
                                        <div>
                                            <span
                                                class="px-2 py-1 text-xs font-bold rounded-full text-white"
                                                :class="{
                                                    'bg-green-500': segment.confidence > 0.8,
                                                    'bg-orange-500':
                                                        segment.confidence > 0.7 &&
                                                        segment.confidence <= 0.8,
                                                    'bg-red-500': segment.confidence <= 0.7,
                                                }"
                                            >
                                                {{ Math.round(segment.confidence * 100) }}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </v-card-text>

            <!-- Action Buttons -->
            <v-card-actions class="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div class="flex justify-end space-x-3 w-full">
                    <button
                        @click="closeCompareModal"
                        class="px-6 py-2.5 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors duration-200 font-medium"
                    >
                        Abbrechen
                    </button>
                    <button
                        @click="
                            markAsUnique(
                                compareData.primary.media_id,
                                compareData.comparison.media_id
                            )
                        "
                        class="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium"
                    >
                        Kein Duplikat
                    </button>
                    <button
                        @click="
                            mergeDuplicates(
                                compareData.primary.media_id,
                                compareData.comparison.media_id
                            )
                        "
                        class="px-6 py-2.5 bg-[#db0720] hover:bg-[#c1061c] text-white rounded-lg transition-colors duration-200 font-medium"
                    >
                        Zusammenführen
                    </button>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script lang="ts" setup>
    import { ref, watch, onMounted, onUnmounted, computed } from 'vue'
    import { useRouter } from 'vue-router'
    import niceNavigation from '@/components/SubNavigations/TheNiceNavigation.vue'
    import useNiceStore from '@/utils/useNiceStore'
    import { useMediaStore } from '@/utils/useMediaStore'
    import VideoThumbnail from '@/components/Media/VideoThumbnail.vue'
    import FullButton from '@/components/Buttons/FullButton.vue'
    import FormInput from '@/components/Offers/FormInput.vue'
    import FormSelect from '@/components/Offers/FormSelect.vue'
    import { v4 as uuidv4 } from 'uuid'
    import type { ActivityLogEntry, ProcessingStats } from '@/stores/NiceStore'
    import {
        mockDeduplicationResponse,
        mockStageProgression,
        mockActivityLogEntries,
    } from '@/constants/mockDeduplicationResponse'

    // Import from store with reactive refs
    const {
        deduplicationReport,
        loading: dedupLoading,
        progress,
        statusMessage,
        errorMessage,
        currentStage,
        currentSubstage,
        elapsedSeconds,
        etaSeconds,
        totalVideos,
        processedVideos,
        duplicateCount,
        detailedStatusMessage,
        stageName,
        substageName,
        formattedElapsedTime,
        formattedEtaTime,
        processingStats,
        activityLog,
        latestActivities,
        stageTimes,
        stageDescription,
        substageDescription,
        resetState,
        runDeduplication,
        getSimilarityDetails,
        getStageDescription,
        getSubstageDescription,
        fetchDeduplicationReport,
        formatMediaId,
    } = useNiceStore()

    // Local state for video deduplication settings
    const userIdInput = ref<string>('')
    const similarityMethod = ref<string>('default')
    const processingPriority = ref<string>('normal')
    const useCache = ref<boolean>(true)
    const savePreferences = ref<boolean>(true)
    const showAdvancedOptions = ref<boolean>(false)

    // UI state
    const showClusters = ref<boolean>(true)
    const showStageTimes = ref<boolean>(false)
    const showActivityLog = ref<boolean>(false)
    const hasRun = ref<boolean>(false)
    const showCompareModal = ref<boolean>(false)
    const compareData = ref<any>(null)
    const selectedComparisonId = ref<string>('')
    const isExecuting = ref<boolean>(false)

    // Marked videos
    const markedForDeletion = ref<Set<string>>(new Set())

    // Debug mode configuration
    const debugMode = ref<boolean>(true)
    const showDebugToggle = ref<boolean>(true) // Set to false to hide the debug toggle
    let debugInterval: number | null = null

    // Additional debug mode variables (not available from store)
    const totalCount = ref<number>(0)
    const currentCount = ref<number>(0)

    // Computed property to ensure debug mode is only active when toggle is shown
    const isDebugModeActive = computed(() => {
        return showDebugToggle.value && debugMode.value
    })

    // Media store
    const mediaStore = useMediaStore()

    // Form ref
    const formRef = ref(null)

    // Computed properties for progress display
    const progressDisplay = computed(() => {
        return progress.value.toFixed(1)
    })

    const safeProgressWidth = computed(() => {
        return Math.max(0, Math.min(100, progress.value))
    })

    // Computed property to check if we have processing stats
    const hasProcessingStats = computed(() => {
        return (
            processingStats.value &&
            (processingStats.value.extraction_speed > 0 ||
                processingStats.value.comparison_speed > 0 ||
                processingStats.value.feature_cache_hits > 0 ||
                processingStats.value.current_memory_usage_mb > 0)
        )
    })

    // Computed property for cache status text
    const cacheStatusText = computed(() => {
        const hits = processingStats.value.feature_cache_hits || 0
        const misses = processingStats.value.feature_cache_misses || 0
        const total = hits + misses

        if (total === 0) return 'Keine Daten'

        const hitRate = (hits / total) * 100
        return `${hits}/${total} (${hitRate.toFixed(0)}%)`
    })

    // Computed property to check if we have valid stage times
    const hasValidStageTimes = computed(() => {
        if (!stageTimes.value || typeof stageTimes.value !== 'object') return false
        return Object.values(stageTimes.value).some(
            (time) => time && typeof time === 'number' && time > 0
        )
    })

    // Labels for similarity components
    const componentLabels: Record<string, string> = {
        visual_similarity: 'Visuelle Ähnlichkeit',
        audio_similarity: 'Audio-Ähnlichkeit',
        metadata_similarity: 'Metadaten-Ähnlichkeit',
        temporal_similarity: 'Zeitliche Ähnlichkeit',
    }

    // Generate a unique job ID
    function generateRandomJobId(): string {
        return uuidv4().replace(/-/g, '').substring(0, 20)
    }

    // Debug mode: Use imported mock deduplication report
    function createDummyDeduplicationReport() {
        // Return the imported mock data with a fresh timestamp
        const report = {
            ...mockDeduplicationResponse,
            timestamp: new Date().toISOString(),
        }

        // DEBUG: Verify links exist in the mock data
        console.log('🔍 Mock data check:', {
            hasLinks: report.duplicate_clusters[0]?.videos[0]?.links?.length || 0,
            firstLink: report.duplicate_clusters[0]?.videos[0]?.links?.[1],
        })

        return report
    }

    // Debug mode: Simulate deduplication process using imported mock data
    async function runDebugDeduplication(): Promise<void> {
        try {
            // Uses mock data from constants/mockDeduplicationResponse.ts
            // DON'T call resetState() in debug mode as it might trigger store calls
            // Instead manually reset only the values we need

            // Set initial state manually for debug mode
            progress.value = 0
            currentStage.value = 'initializing'
            currentSubstage.value = 'setup'
            statusMessage.value = 'Initialisiere Debug-Deduplikation...'
            elapsedSeconds.value = 0
            hasRun.value = true
            errorMessage.value = undefined

            // Reset debug-specific counters
            totalCount.value = 0
            currentCount.value = 0
            totalVideos.value = 0
            processedVideos.value = 0
            duplicateCount.value = 0

            // Initialize processing stats for debug mode
            processingStats.value = {
                videos_per_second: 0,
                pairs_per_second: 0,
                current_memory_usage_mb: 0,
                peak_memory_usage_mb: 0,
                last_extracted_video: '',
                last_compared_pair: '',
                extraction_speed: 0,
                comparison_speed: 0,
                feature_cache_hits: 0,
                feature_cache_misses: 0,
            }

            // Initialize other debug values
            stageTimes.value = {}
            etaSeconds.value = null

            // Stage progression for debug mode
            const debugStages = mockStageProgression

            let totalElapsed = 0
            let activityLogIndex = 0

            // Initialize activity log for debug mode
            activityLog.value = []

            // Initialize processing stats values that we'll use
            processingStats.value.videos_per_second = 0
            processingStats.value.pairs_per_second = 0
            processingStats.value.current_memory_usage_mb = 0
            processingStats.value.peak_memory_usage_mb = 0
            processingStats.value.last_extracted_video = ''
            processingStats.value.last_compared_pair = ''
            processingStats.value.extraction_speed = 0
            processingStats.value.comparison_speed = 0
            processingStats.value.feature_cache_hits = 0
            processingStats.value.feature_cache_misses = 0

            // Process stages
            for (const [index, stageInfo] of debugStages.entries()) {
                if (!hasRun.value) break // Check if cancelled

                // Update stage
                currentStage.value = stageInfo.stage
                currentSubstage.value = stageInfo.substage
                progress.value = stageInfo.progress

                // Update processing stats based on stage
                if (stageInfo.stage === 'feature_extraction') {
                    totalVideos.value = mockDeduplicationResponse.stats.total_videos
                    processedVideos.value = Math.floor(
                        ((stageInfo.progress - 20) / 35) * totalVideos.value
                    )
                    processingStats.value.extraction_speed = 45.2
                    processingStats.value.current_memory_usage_mb = 1250 + Math.random() * 200
                    processingStats.value.feature_cache_hits = Math.floor(
                        processedVideos.value * 0.65
                    )
                    processingStats.value.feature_cache_misses =
                        processedVideos.value - processingStats.value.feature_cache_hits
                    processingStats.value.last_extracted_video =
                        mockDeduplicationResponse.duplicate_clusters[0].videos[
                            Math.floor(Math.random() * 3)
                        ].media_id
                } else if (stageInfo.stage === 'comparison') {
                    totalCount.value =
                        (mockDeduplicationResponse.stats.total_videos *
                            (mockDeduplicationResponse.stats.total_videos - 1)) /
                        2
                    currentCount.value = Math.floor(
                        ((stageInfo.progress - 55) / 20) * totalCount.value
                    )
                    processingStats.value.comparison_speed = 180.7
                    const randomCluster =
                        mockDeduplicationResponse.duplicate_clusters[
                            Math.floor(
                                Math.random() * mockDeduplicationResponse.duplicate_clusters.length
                            )
                        ]
                    const video1 = randomCluster.videos[0]
                    const video2 = randomCluster.videos[1]
                    processingStats.value.last_compared_pair = `${video1.media_id}-${video2.media_id}`
                } else if (stageInfo.stage === 'clustering') {
                    duplicateCount.value = mockDeduplicationResponse.stats.duplicate_pairs
                }

                // Update elapsed time
                totalElapsed += stageInfo.duration
                elapsedSeconds.value = totalElapsed / 1000

                // Calculate ETA
                if (stageInfo.progress < 100) {
                    const remainingProgress = 100 - stageInfo.progress
                    etaSeconds.value =
                        ((totalElapsed / stageInfo.progress) * remainingProgress) / 1000
                }

                // Add activity log entries
                if (activityLogIndex < mockActivityLogEntries.length && stageInfo.progress > 0) {
                    // Add appropriate number of log entries based on progress
                    const entriesPerStage = Math.ceil(
                        mockActivityLogEntries.length / debugStages.length
                    )
                    const stageBoundary = (index + 1) * entriesPerStage

                    while (
                        activityLogIndex < Math.min(stageBoundary, mockActivityLogEntries.length)
                    ) {
                        activityLog.value.push({
                            timestamp: new Date().toISOString(),
                            activity: `${mockActivityLogEntries[activityLogIndex]} ${userIdInput.value}`,
                        })
                        activityLogIndex++
                    }
                }

                // Update stage times
                if (index > 0) {
                    const prevStage = debugStages[index - 1].stage
                    if (!stageTimes.value[prevStage]) {
                        stageTimes.value[prevStage] = 0
                    }
                    stageTimes.value[prevStage] += debugStages[index - 1].duration / 1000
                }

                // For the current stage being processed
                if (!stageTimes.value[stageInfo.stage]) {
                    stageTimes.value[stageInfo.stage] = 0
                }

                // Wait for the stage duration
                await new Promise((resolve) => {
                    debugInterval = window.setTimeout(resolve, stageInfo.duration)
                })
            }

            // Set final report when completed
            if (hasRun.value && progress.value === 100) {
                deduplicationReport.value = createDummyDeduplicationReport()

                // DEBUG: Check if links exist after setting
                console.log('🔍 After setting report:', {
                    hasReport: !!deduplicationReport.value,
                    firstVideoHasLinks: !!deduplicationReport.value?.duplicate_clusters?.[0]?.videos?.[0]?.links,
                    linksCount: deduplicationReport.value?.duplicate_clusters?.[0]?.videos?.[0]?.links?.length,
                })

                currentStage.value = 'completed'
                statusMessage.value = 'Debug-Deduplikation abgeschlossen'
                etaSeconds.value = null
                debugDeduplicationData()

                // Update final stage time and ensure all stages have times
                stageTimes.value['completed'] = 0.2
                stageTimes.value['report_generation'] = stageTimes.value['report_generation'] || 0.3
                stageTimes.value['clustering'] = stageTimes.value['clustering'] || 1.0
                stageTimes.value['comparison'] = stageTimes.value['comparison'] || 1.8
                stageTimes.value['feature_extraction'] =
                    stageTimes.value['feature_extraction'] || 2.4
                stageTimes.value['api_fetch'] = stageTimes.value['api_fetch'] || 1.2
                stageTimes.value['initializing'] = stageTimes.value['initializing'] || 0.8

                // Add final activity log entry if not already added
                if (activityLogIndex < mockActivityLogEntries.length) {
                    activityLog.value.push({
                        timestamp: new Date().toISOString(),
                        activity: mockActivityLogEntries[mockActivityLogEntries.length - 1],
                    })
                }
            }
        } catch (error) {
            console.error('Debug deduplication error:', error)
            errorMessage.value = 'Debug-Deduplikation fehlgeschlagen'
            currentStage.value = 'error'
            hasRun.value = true // Keep hasRun true to show the error state
        } finally {
            dedupLoading.value = false
            isExecuting.value = false
        }
    }

    // Execute deduplication (with debug mode support)
    async function executeDeduplication(): Promise<void> {
        // Prevent double execution
        if (isExecuting.value || dedupLoading.value) {
            console.log('Deduplication already in progress, ignoring click')
            return
        }

        if (!userIdInput.value) {
            alert('Bitte geben Sie eine Benutzer‑ID ein')
            return
        }

        // Set local execution flag immediately
        isExecuting.value = true
        dedupLoading.value = true

        try {
            if (isDebugModeActive.value) {
                // DEBUG MODE: Use imported mock data from constants/mockDeduplicationResponse.ts
                console.log(
                    'Running debug mode deduplication with mock data - bypassing all backend calls...'
                )
                clearDeduplicationState()
                markedForDeletion.value.clear()
                showActivityLog.value = true
                await runDebugDeduplication()
            } else {
                // PRODUCTION MODE: Run real deduplication
                console.log('Running production mode deduplication...')

                // Reset any previous state first to ensure clean start
                resetState()
                clearDeduplicationState()

                // Mark that a run has started
                hasRun.value = true
                markedForDeletion.value.clear()
                showActivityLog.value = true

                // Save initial state
                saveDeduplicationState()

                // Generate random job ID and run real deduplication
                const newJobId = generateRandomJobId()
                const options = {
                    method: similarityMethod.value,
                    priority: processingPriority.value,
                    useCache: useCache.value,
                }

                // Start the deduplication process with the store
                await runDeduplication(userIdInput.value, newJobId, options)
            }
        } catch (error) {
            console.error('Error during deduplication:', error)
            alert('Fehler beim Starten der Deduplikation. Bitte versuchen Sie es erneut.')
        } finally {
            // Always reset the execution flag
            isExecuting.value = false
            if (isDebugModeActive.value) {
                dedupLoading.value = false
            }
        }
    }

    // Cancel deduplication process (updated for debug mode)
    function cancelDeduplication(): void {
        if (confirm('Möchten Sie die Deduplikation wirklich abbrechen?')) {
            // Clear debug interval if running
            if (debugInterval !== null) {
                window.clearTimeout(debugInterval)
                debugInterval = null
            }

            // Reset debug-specific variables
            totalCount.value = 0
            currentCount.value = 0

            resetState()
            hasRun.value = false
            clearDeduplicationState()
            isExecuting.value = false
            dedupLoading.value = false
        }
    }

    // Save deduplication state to localStorage
    function saveDeduplicationState(): void {
        try {
            const state = {
                hasRun: hasRun.value,
                userIdInput: userIdInput.value,
                showActivityLog: showActivityLog.value,
                showClusters: showClusters.value,
                showStageTimes: showStageTimes.value,
                debugMode: debugMode.value,
                // Store a snapshot of the store state
                deduplicationReport: deduplicationReport.value,
                progress: progress.value,
                currentStage: currentStage.value,
                currentSubstage: currentSubstage.value,
                stageName: stageName.value,
                substageName: substageName.value,
                detailedStatusMessage: detailedStatusMessage.value,
                errorMessage: errorMessage.value,
                processingStats: processingStats.value,
                latestActivities: latestActivities.value,
                stageTimes: stageTimes.value,
                stageDescription: stageDescription.value,
                substageDescription: substageDescription.value,
                timestamp: new Date().toISOString(),
            }
            localStorage.setItem('dedup-state', JSON.stringify(state))
        } catch (e) {
            console.warn('Could not save deduplication state to localStorage:', e)
        }
    }

    // Load deduplication state from localStorage
    function loadDeduplicationState(): void {
        try {
            const savedState = localStorage.getItem('dedup-state')
            if (savedState) {
                const state = JSON.parse(savedState)

                // Check if the state is not too old (e.g., less than 24 hours)
                const savedTime = new Date(state.timestamp)
                const now = new Date()
                const hoursDiff = (now.getTime() - savedTime.getTime()) / (1000 * 60 * 60)

                if (hoursDiff < 24) {
                    // Restore UI state
                    hasRun.value = state.hasRun || false
                    userIdInput.value = state.userIdInput || ''
                    showActivityLog.value = state.showActivityLog || false
                    showClusters.value =
                        state.showClusters !== undefined ? state.showClusters : true
                    showStageTimes.value = state.showStageTimes || false
                    debugMode.value = state.debugMode || false

                    // If we have a completed deduplication report, restore it
                    if (state.deduplicationReport && state.progress >= 99) {
                        // Note: We can't directly set the store values here since they're reactive
                        // But we can set the report if it exists
                        console.log('Restored deduplication state from localStorage')
                    }
                } else {
                    // Clear old state
                    localStorage.removeItem('dedup-state')
                }
            }
        } catch (e) {
            console.warn('Could not load deduplication state from localStorage:', e)
            localStorage.removeItem('dedup-state')
        }
    }

    // Clear deduplication state from localStorage
    function clearDeduplicationState(): void {
        localStorage.removeItem('dedup-state')
    }

    // Reset everything (including deduplication results)
    function resetEverything(): void {
        // Clear debug interval if running
        if (debugInterval !== null) {
            window.clearTimeout(debugInterval)
            debugInterval = null
        }

        userIdInput.value = ''
        hasRun.value = false
        markedForDeletion.value.clear()
        showActivityLog.value = false

        // Reset debug-specific variables
        totalCount.value = 0
        currentCount.value = 0

        resetState() // This calls the store's resetState method
        clearDeduplicationState() // Clear localStorage

        // Also reset any component-specific state
        showCompareModal.value = false
        compareData.value = null
        isExecuting.value = false
        dedupLoading.value = false
    }

    // Save user preferences to localStorage
    function saveUserPreferences(): void {
        const preferences = {
            method: similarityMethod.value,
            priority: processingPriority.value,
            useCache: useCache.value,
            showAdvancedOptions: showAdvancedOptions.value,
            showClusters: showClusters.value,
            debugMode: showDebugToggle.value ? debugMode.value : false, // Only save debug mode if toggle is shown
            lastUserId: userIdInput.value,
        }
        localStorage.setItem('dedup-preferences', JSON.stringify(preferences))
    }

    // Load user preferences from localStorage
    function loadUserPreferences(): void {
        const savedPrefs = localStorage.getItem('dedup-preferences')
        if (savedPrefs) {
            try {
                const prefs = JSON.parse(savedPrefs)
                similarityMethod.value = prefs.method || 'default'
                processingPriority.value = prefs.priority || 'normal'
                useCache.value = prefs.useCache !== undefined ? prefs.useCache : true
                showAdvancedOptions.value = prefs.showAdvancedOptions || false
                showClusters.value = prefs.showClusters !== undefined ? prefs.showClusters : true
                // Only load debug mode if debug toggle is shown
                debugMode.value = showDebugToggle.value ? prefs.debugMode || false : false
            } catch (e) {
                // Silent error for preferences loading
            }
        }
    }

    // Export results in various formats
    function exportResults(format: string): void {
        if (!deduplicationReport.value) {
            alert('Keine Deduplikationsergebnisse zum Exportieren vorhanden')
            return
        }
        try {
            const fileName = `deduplication-results-${new Date().toISOString().substring(0, 10)}`
            let content = ''
            let dataType = ''
            let fileExtension = ''

            if (format === 'json') {
                content = JSON.stringify(deduplicationReport.value, null, 2)
                dataType = 'application/json'
                fileExtension = 'json'
            } else if (format === 'csv') {
                content = convertToCSV(deduplicationReport.value)
                dataType = 'text/csv'
                fileExtension = 'csv'
            } else if (format === 'pdf') {
                alert('PDF‑Export ist in der Demo‑Version nicht verfügbar')
                return
            }

            const blob = new Blob([content], { type: dataType })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `${fileName}.${fileExtension}`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
        } catch (error) {
            alert('Export der Ergebnisse fehlgeschlagen')
        }
    }

    // Convert deduplication report to CSV format
    function convertToCSV(report: any): string {
        let csv = 'Cluster,Media ID 1,Media ID 2,Titel 1,Titel 2,Ähnlichkeit\n'
        if (report.duplicate_clusters) {
            report.duplicate_clusters.forEach((cluster: any, index: number) => {
                if (!cluster.videos || cluster.videos.length < 2) return
                for (let i = 0; i < cluster.videos.length; i++) {
                    const video1 = cluster.videos[i]
                    for (let j = i + 1; j < cluster.videos.length; j++) {
                        const video2 = cluster.videos[j]
                        let similarity = 0
                        if (video1.similar_videos) {
                            const found = video1.similar_videos.find(
                                (sv: any) => sv.media_id === video2.media_id
                            )
                            if (found) similarity = found.similarity
                        }
                        csv += `${index + 1},${video1.media_id},${video2.media_id},"${video1.title}","${video2.title}",${similarity}\n`
                    }
                }
            })
        }
        return csv
    }

    // Format processing time to human-readable string
    function formatProcessingTime(seconds: number): string {
        if (seconds < 60) {
            return `${seconds.toFixed(1)} s`
        } else if (seconds < 3600) {
            const minutes = Math.floor(seconds / 60)
            const remainingSeconds = seconds % 60
            return `${minutes}m ${remainingSeconds.toFixed(0)}s`
        } else {
            const hours = Math.floor(seconds / 3600)
            const minutes = Math.floor((seconds % 3600) / 60)
            return `${hours}h ${minutes}m`
        }
    }

    // Format time seconds to human-readable string
    function formatTime(seconds: number): string {
        if (seconds < 60) {
            return `${seconds.toFixed(1)}s`
        } else if (seconds < 3600) {
            const minutes = Math.floor(seconds / 60)
            const remainingSeconds = Math.floor(seconds % 60)
            return `${minutes}m ${remainingSeconds}s`
        } else {
            const hours = Math.floor(seconds / 3600)
            const minutes = Math.floor((seconds % 3600) / 60)
            return `${hours}h ${minutes}m`
        }
    }

    // Calculate percentage of total time
    function calculatePercentage(stageTime: number): string {
        if (!stageTime || typeof stageTime !== 'number') return '0'

        const totalTime = Object.values(stageTimes.value)
            .filter((time) => time && typeof time === 'number')
            .reduce((sum, time) => sum + time, 0)

        if (totalTime === 0) return '0'
        return ((stageTime / totalTime) * 100).toFixed(1)
    }

    // Get stage display name
    function getStageDisplayName(stage: string): string {
        const stageDisplayNames: Record<string, string> = {
            initializing: 'Initialisierung',
            api_fetch: 'API-Datenabruf',
            feature_extraction: 'Merkmalsextraktion',
            comparison: 'Videovergleich',
            clustering: 'Clustering',
            report_generation: 'Berichtserstellung',
            completed: 'Abschluss',
            error: 'Fehler',
        }
        return stageDisplayNames[stage] || stage
    }

    // Format timestamp to human-readable format
    function formatTimestamp(isoString: string): string {
        try {
            const date = new Date(isoString)
            return date.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            })
        } catch (e) {
            return isoString
        }
    }

    // Mark a video for deletion
    function markForDeletion(mediaId: string): void {
        markedForDeletion.value.add(mediaId)
        alert(
            `Demomodus: ${formatMediaId(mediaId)} zum Löschen markiert (keine tatsächliche Löschung)`
        )
    }

    // Open comparison modal
    async function openCompareModal(mediaId: string, cluster: any): Promise<void> {
        if (!cluster || !cluster.videos) return
        const primaryVideo = cluster.videos.find((v: any) => v.media_id === mediaId)
        if (!primaryVideo) return

        const otherVideos = cluster.videos
            .filter((v: any) => v.media_id !== mediaId)
            .map((v: any) => {
                let similarity = 0
                if (primaryVideo.similar_videos) {
                    const found = primaryVideo.similar_videos.find(
                        (sv: any) => sv.media_id === v.media_id
                    )
                    if (found) similarity = found.similarity
                }
                return { ...v, similarity }
            })

        if (otherVideos.length === 0) {
            alert('Keine weiteren Videos in dieser Gruppe zum Vergleichen vorhanden')
            return
        }

        // Get the first comparison video
        const firstComparisonVideo = otherVideos[0]

        // Get similarity details from the store or generate them
        let detailedSimilarity = null

        if (!isDebugModeActive.value) {
            try {
                const details = await getSimilarityDetails(
                    primaryVideo.media_id,
                    firstComparisonVideo.media_id
                )

                if (details) {
                    detailedSimilarity = details
                } else {
                    // Fallback to generated details if needed
                    detailedSimilarity = generateSimilarityDetails(
                        primaryVideo,
                        firstComparisonVideo
                    )
                }
            } catch (error) {
                detailedSimilarity = generateSimilarityDetails(primaryVideo, firstComparisonVideo)
            }
        } else {
            // In debug mode, always generate similarity details
            detailedSimilarity = generateSimilarityDetails(primaryVideo, firstComparisonVideo)
        }

        compareData.value = {
            primary: primaryVideo,
            comparison: firstComparisonVideo,
            otherVideos,
            similarityDetails: detailedSimilarity,
        }
        selectedComparisonId.value = firstComparisonVideo.media_id
        showCompareModal.value = true
    }

    // Update the comparison video when selection changes
    async function updateComparisonVideo(): Promise<void> {
        if (!compareData.value || !compareData.value.otherVideos) return
        const selected = compareData.value.otherVideos.find(
            (v: any) => v.media_id === selectedComparisonId.value
        )
        if (selected) {
            compareData.value.comparison = selected

            // Get similarity details from the store or generate them
            let detailedSimilarity = null

            if (!isDebugModeActive.value) {
                try {
                    const details = await getSimilarityDetails(
                        compareData.value.primary.media_id,
                        selected.media_id
                    )

                    if (details) {
                        detailedSimilarity = details
                    } else {
                        // Fallback to generated details
                        detailedSimilarity = generateSimilarityDetails(
                            compareData.value.primary,
                            selected
                        )
                    }
                } catch (error) {
                    detailedSimilarity = generateSimilarityDetails(
                        compareData.value.primary,
                        selected
                    )
                }
            } else {
                // In debug mode, always generate similarity details
                detailedSimilarity = generateSimilarityDetails(compareData.value.primary, selected)
            }

            compareData.value.similarityDetails = detailedSimilarity
        }
    }

    // Generate detailed similarity comparison as fallback
    function generateSimilarityDetails(video1: any, video2: any): any {
        let similarity = 0
        if (video1.similar_videos) {
            const found = video1.similar_videos.find((sv: any) => sv.media_id === video2.media_id)
            if (found) similarity = found.similarity
        }

        // Create deterministic components from media IDs
        const idStr = video1.media_id + video2.media_id
        const hash = hashCode(idStr)

        // Generate components based on hash value
        const visualFactor = 0.8 + (hash % 40) / 100
        const audioFactor = 0.7 + (hash % 40) / 100
        const metadataFactor = 0.9 + (hash % 20) / 100
        const temporalFactor = 0.6 + (hash % 50) / 100

        return {
            overall: similarity,
            components: {
                visual_similarity: Math.min(0.95, Math.max(0.4, similarity * visualFactor)),
                audio_similarity: Math.min(0.95, Math.max(0.3, similarity * audioFactor)),
                metadata_similarity: Math.min(0.95, Math.max(0.5, similarity * metadataFactor)),
                temporal_similarity: Math.min(0.95, Math.max(0.2, similarity * temporalFactor)),
            },
            matching_segments: [
                {
                    start: `00:${(hash % 15).toString().padStart(2, '0')}:${(hash % 59).toString().padStart(2, '0')}`,
                    duration: `00:${(1 + (hash % 5)).toString().padStart(2, '0')}:${(hash % 59).toString().padStart(2, '0')}`,
                    confidence: 0.7 + (hash % 25) / 100,
                },
                {
                    start: `00:${((hash + 10) % 59).toString().padStart(2, '0')}:${(hash % 59).toString().padStart(2, '0')}`,
                    duration: `00:${(1 + (hash % 3)).toString().padStart(2, '0')}:${(hash % 59).toString().padStart(2, '0')}`,
                    confidence: 0.65 + (hash % 30) / 100,
                },
            ],
        }
    }

    // Simple hash function for deterministic results
    function hashCode(str: string): number {
        let hash = 0
        for (let i = 0; i < str.length; i++) {
            hash = (hash << 5) - hash + str.charCodeAt(i)
            hash |= 0 // Convert to 32bit integer
        }
        return Math.abs(hash)
    }

    // Format languages array to readable string
    function formatLanguages(languages: string[] | undefined): string {
        if (!languages || languages.length === 0) return 'Unbekannt'
        return languages.join(', ')
    }

    // Actions for the comparison modal (demo only)
    function markAsUnique(mediaId1: string, mediaId2: string): void {
        alert(
            `${isDebugModeActive.value ? 'Debug-' : 'Demo'}modus: ${formatMediaId(mediaId1)} und ${formatMediaId(mediaId2)} als einzigartig markiert`
        )
        showCompareModal.value = false
    }

    function mergeDuplicates(mediaId1: string, mediaId2: string): void {
        alert(
            `${isDebugModeActive.value ? 'Debug-' : 'Demo'}modus: ${formatMediaId(mediaId1)} und ${formatMediaId(mediaId2)} zusammengeführt`
        )
        showCompareModal.value = false
    }

    function deleteVideo(mediaId: string): void {
        markedForDeletion.value.add(mediaId)
        alert(
            `${isDebugModeActive.value ? 'Debug-' : 'Demo'}modus: ${formatMediaId(mediaId)} zum Löschen markiert`
        )
        showCompareModal.value = false
    }

    // Close compare modal
    function closeCompareModal(): void {
        showCompareModal.value = false
    }

    // Generate a thumbnail URL based on media ID and video data
    const getThumbnailUrl = (mediaId: string, videoData?: any): string => {
        // Force check the actual deduplication report if in debug mode
        if (isDebugModeActive.value && deduplicationReport.value) {
            // Find the video in the actual report
            for (const cluster of deduplicationReport.value.duplicate_clusters) {
                const video = cluster.videos.find((v: any) => v.media_id === mediaId)
                if (video && video.links) {
                    const x2Thumbnail = video.links.find((link: any) =>
                        link.rel === 'media/preview/thumbnail/x2'
                    )
                    if (x2Thumbnail?.href) {
                        return x2Thumbnail.href
                    }

                    const thumbnail = video.links.find((link: any) =>
                        link.rel === 'media/preview/thumbnail'
                    )
                    if (thumbnail?.href) {
                        return thumbnail.href
                    }
                }
            }
        }

        // Original logic for non-debug mode
        if (videoData?.thumbnail_url) {
            return videoData.thumbnail_url
        }

        if (videoData?.links && Array.isArray(videoData.links)) {
            const x2Thumbnail = videoData.links.find((link: any) =>
                link.rel === 'media/preview/thumbnail/x2'
            )
            if (x2Thumbnail?.href) {
                return x2Thumbnail.href
            }

            const thumbnail = videoData.links.find((link: any) =>
                link.rel === 'media/preview/thumbnail'
            )
            if (thumbnail?.href) {
                return thumbnail.href
            }
        }

        // This should never be reached if data is correct
        console.warn('⚠️ Falling back to placeholder for:', mediaId)
        const hash = mediaId.split('').reduce((a, b) => {
            a = (a << 5) - a + b.charCodeAt(0)
            return a & a
        }, 0)

        return `https://picsum.photos/seed/${Math.abs(hash)}/640/360`
    }

    function handleImageError(event: Event, mediaId: string): void {
        console.error('❌ Image failed to load for mediaId:', mediaId)
        const img = event.target as HTMLImageElement
        console.error('Failed URL:', img.src)

        // Try a fallback URL with a simpler approach
        const fallbackSeed = mediaId.charCodeAt(0) + mediaId.charCodeAt(1)
        const fallbackUrl = `https://picsum.photos/seed/${fallbackSeed}/640/360`
        console.log('🔄 Trying fallback URL:', fallbackUrl)

        // First try the fallback URL
        img.onerror = () => {
            console.error('❌ Fallback also failed, using SVG placeholder')
            img.src =
                'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQwIiBoZWlnaHQ9IjM2MCIgdmlld0JveD0iMCAwIDY0MCAzNjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI2NDAiIGhlaWdodD0iMzYwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yNTYgMTgwTDMwNCAyMDhWMTUyTDI1NiAxODBaIiBmaWxsPSIjOUI5QkEzIi8+CjxwYXRoIGQ9Ik0zMjAgMTgwQzMyMCAxOTMuMjU1IDMwOS4yNTUgMjA0IDI5NiAyMDRDMjgyLjc0NSAyMDQgMjcyIDE5My4yNTUgMjcyIDE4MEMyNzIgMTY2Ljc0NSAyODIuNzQ1IDE1NiAyOTYgMTU2QzMwOS4yNTUgMTU2IDMyMCAxNjYuNzQ1IDMyMCAxODBaIiBmaWxsPSIjOUI5QkEzIi8+Cjx0ZXh0IHg9IjMyMCIgeT0iMjAwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNkI3MjgwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiPktlaW4gQmlsZCB2ZXJmw7xnYmFyPC90ZXh0Pgo8L3N2Zz4K'
        }

        img.src = fallbackUrl
    }

    function debugDeduplicationData(): void {
        console.log('🔍 === DEBUGGING DEDUPLICATION DATA ===')
        console.log('debugMode:', debugMode.value)
        console.log('isDebugModeActive:', isDebugModeActive.value)
        console.log('deduplicationReport exists:', !!deduplicationReport.value)

        if (deduplicationReport.value?.duplicate_clusters) {
            console.log('Number of clusters:', deduplicationReport.value.duplicate_clusters.length)

            deduplicationReport.value.duplicate_clusters.forEach((cluster: any, index: number) => {
                console.log(`\nCluster ${index + 1}:`)
                cluster.videos.forEach((video: any, vidIndex: number) => {
                    const thumbnailUrl = getThumbnailUrl(video.media_id, video)
                    console.log(`  - Video ${vidIndex + 1}:`, {
                        media_id: video.media_id,
                        title: video.title,
                        thumbnail_url: thumbnailUrl,
                        has_links: !!video.links,
                        links_count: video.links?.length || 0,
                        video_keys: Object.keys(video),
                    })

                    // Log first link if exists
                    if (video.links && video.links.length > 0) {
                        console.log(`    First link:`, video.links[0])
                    }
                })
            })
        } else {
            console.log('❌ No duplicate clusters found in deduplicationReport')
        }
        console.log('🔍 === END DEBUG ===')
    }

    // Router for navigation
    const router = useRouter()

    // Play video function
    function playVideo(mediaId: string): void {
        // Update the media store with this video
        mediaStore.playVideo(mediaId)

        // Navigate to video player page in the same window
        router.push(`/media/player/${mediaId}`)

        // Also close the compare modal if it's open
        if (showCompareModal.value) {
            showCompareModal.value = false
        }
    }

    // Watch for progress changes to save state and fetch report when needed
    watch(progress, async (newVal, oldVal) => {
        if (newVal !== oldVal) {
            // Save state whenever progress changes
            if (hasRun.value) {
                saveDeduplicationState()
            }

            // When progress reaches 100% and we don't have a report yet, fetch it explicitly (only in non-debug mode)
            if (
                !isDebugModeActive.value &&
                newVal >= 100 &&
                !deduplicationReport.value &&
                compareData.value?.jobId
            ) {
                try {
                    await fetchDeduplicationReport(compareData.value.jobId)
                } catch (error) {
                    // Silent error handling
                }
            }
        }
    })

    // Watch for deduplication report changes to save state
    watch(deduplicationReport, (newReport) => {
        if (newReport) {
            showClusters.value = true
            saveDeduplicationState()
        }
    })

    // Watch for other state changes to save
    watch([currentStage, stageName, detailedStatusMessage, errorMessage], () => {
        if (hasRun.value) {
            saveDeduplicationState()
        }
    })

    // Watch for status-related errors to automatically display activity log
    watch(errorMessage, (newError) => {
        if (newError) {
            showActivityLog.value = true
        }
    })

    // Watch debug mode changes to save preferences
    watch(isDebugModeActive, (newVal) => {
        saveUserPreferences()
    })

    // Force debug mode to false when debug toggle is hidden
    watch(
        showDebugToggle,
        (newVal) => {
            if (!newVal) {
                debugMode.value = false
            }
        },
        { immediate: true }
    )

    // Load preferences and state on mount
    onMounted(() => {
        loadUserPreferences()
        loadDeduplicationState()
    })

    // Cleanup on unmount
    onUnmounted(() => {
        if (debugInterval !== null) {
            window.clearTimeout(debugInterval)
            debugInterval = null
        }
    })

    //breadcrumbs
    const breadcrumbs = [
        {
            title: 'NICE',
            disabled: false,
            href: '/nice',
        },
        {
            title: 'Videoduplikate',
            disabled: true,
            href: '/nice/deduplication',
        },
    ]
</script>

<style scoped>
    /* Line clamp utilities */
    .line-clamp-1 {
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .line-clamp-2 {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .line-clamp-3 {
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    /* Video thumbnail hover effects */
    .video-thumbnail-static {
        transition:
            transform 0.3s ease,
            box-shadow 0.3s ease;
    }

    .video-thumbnail-static:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    }

    /* Custom scrollbar for descriptions */
    .overflow-y-auto::-webkit-scrollbar {
        width: 4px;
    }

    .overflow-y-auto::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 2px;
    }

    .overflow-y-auto::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 2px;
    }

    .overflow-y-auto::-webkit-scrollbar-thumb:hover {
        background: #a8a8a8;
    }

    /* Smooth transitions for interactive elements */
    button,
    .cursor-pointer {
        transition: all 0.2s ease;
    }

    /* Enhanced modal backdrop */
    .comparison-modal .v-overlay__content {
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    }

    /* Improved form controls */
    .v-select .v-field {
        border-radius: 8px;
    }

    /* Better focus states */
    button:focus {
        outline: 2px solid #db0720;
        outline-offset: 2px;
    }

    /* Loading state animations */
    @keyframes pulse {
        0%,
        100% {
            opacity: 1;
        }
        50% {
            opacity: 0.5;
        }
    }

    .animate-pulse {
        animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }

    /* Enhanced card shadows */
    .shadow-sm {
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    }

    .shadow-md {
        box-shadow:
            0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
    }

    .shadow-lg {
        box-shadow:
            0 10px 15px -3px rgba(0, 0, 0, 0.1),
            0 4px 6px -2px rgba(0, 0, 0, 0.05);
    }

    /* Transform utilities */
    .rotate-180 {
        transform: rotate(180deg);
    }

    /* Improved spacing for grid layouts */
    .gap-6 > * {
        margin: 0;
    }

    /* Better visual hierarchy */
    h3,
    h4,
    h5 {
        letter-spacing: -0.025em;
    }

    /* Enhanced gradient backgrounds */
    .bg-gradient-to-r {
        background-image: linear-gradient(to right, var(--tw-gradient-stops));
    }

    .bg-gradient-to-br {
        background-image: linear-gradient(to bottom right, var(--tw-gradient-stops));
    }

    /* Improved border radius */
    .rounded-xl {
        border-radius: 0.75rem;
    }

    .rounded-2xl {
        border-radius: 1rem;
    }

    /* Better aspect ratio handling */
    .aspect-video {
        aspect-ratio: 16 / 9;
    }

    /* Enhanced color utilities for similarity badges */
    .bg-green-500 {
        background-color: rgb(34 197 94);
    }

    .bg-orange-500 {
        background-color: rgb(249 115 22);
    }

    .bg-red-500 {
        background-color: rgb(239 68 68);
    }

    .text-green-800 {
        color: rgb(22 101 52);
    }

    .text-orange-800 {
        color: rgb(154 52 18);
    }

    .text-red-800 {
        color: rgb(153 27 27);
    }

    /* Improved hover states */
    .hover\:scale-105:hover {
        transform: scale(1.05);
    }

    .hover\:scale-100:hover {
        transform: scale(1);
    }

    /* Better transition timing */
    .transition-all {
        transition-property: all;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: 150ms;
    }

    .transition-transform {
        transition-property: transform;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: 150ms;
    }

    .transition-colors {
        transition-property:
            color, background-color, border-color, text-decoration-color, fill, stroke;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: 150ms;
    }

    .transition-opacity {
        transition-property: opacity;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: 150ms;
    }

    .duration-200 {
        transition-duration: 200ms;
    }

    .duration-300 {
        transition-duration: 300ms;
    }

    .duration-500 {
        transition-duration: 500ms;
    }
</style>
